import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import { useAuthStore } from "./authStore";
import { useSnackStore } from "./snackStore";
import { useI18n } from "vue-i18n";
import { useSettingsStore } from "@/stores/settingsStore.ts";
import { useTheme } from "vuetify/lib/composables/theme.mjs";

export const useJitsiStore = defineStore("jitsi", () => {
  const { loggedAsStaff, isLoggedIn } = storeToRefs(useAuthStore());
  const settingsStore = useSettingsStore();
  const snackStore = useSnackStore();
  const { t } = useI18n();
  const theme = useTheme();
  const userInfoConfig = isLoggedIn.value
    ? { displayName: loggedAsStaff.value?.first_name + " " + loggedAsStaff.value?.last_name }
    : {};
  const inMeeting = ref<boolean>(false);
  const meetingDiv = ref<HTMLElement | null>(null);
  const jitsiAPI = ref<any>(null);

  const prepareId = ref<string | null>(null);

  const isMuted = ref<boolean>(false);
  const isVideoMuted = ref<boolean>(false);

  const prepareJoinRoom = (roomId: string) => {
    prepareId.value = roomId;
  };

  const joinRoom = (roomId: string) => {
    inMeeting.value = true;
    if (!meetingDiv.value) {
      snackStore.showError(t("integrations.jitsi.error.divNotSet"));
      return;
    }
    jitsiAPI.value = new window.JitsiMeetExternalAPI(import.meta.env.VITE_JITSI_SERVER, {
      roomName: roomId,
      parentNode: meetingDiv.value,
      height: "100%",
      width: "100%",
      lang: settingsStore.language,
      userInfo: userInfoConfig,
      configOverwrite: {
        filmStrip: {
          disabled: true,
          disableTopPanel: true,
        },
        // hite buttons toolbarButtons: [
        // ],
        conferenceInfo: {
          autoHide: ["ParticipantsPanel"],
        },
        enableClosePage: true,
        startWithAudioMuted: true,
        prejoinPageEnabled: false,
        enableWelcomePage: false,
        disableEndForAll: true,
        prejoinConfig: {
          enabled: !isLoggedIn.value,
        },
      },
      interfaceConfigOverwrite: {
        VERTICAL_FILMSTRIP: false,
        JITSI_WATERMARK_LINK: "https://example.com",
        DEFAULT_BACKGROUND: theme.current.value.colors.primary,
        SHOW_BRAND_WATERMARK: true,
        SHOW_WATERMARK_FOR_GUESTS: false,
      },
    });
    jitsiAPI.value.addEventListener("videoConferenceLeft", () => {
      leaveRoom();
    });

    jitsiAPI.value.addListener("audioMuteStatusChanged", (event: { muted: boolean }) => {
      isMuted.value = event.muted;
    });
    jitsiAPI.value.addListener("videoMuteStatusChanged", (event: { muted: boolean }) => {
      isVideoMuted.value = event.muted;
    });
  };

  const leaveRoom = () => {
    if (inMeeting.value) {
      inMeeting.value = false;
    }
    if (jitsiAPI) {
      jitsiAPI.value.dispose();
      jitsiAPI.value = null;
    }
    prepareId.value = null;
  };

  const toggleVideo = () => {
    if (jitsiAPI && jitsiAPI.value) {
      jitsiAPI.value.executeCommand("toggleVideo");
    }
  };

  const toggleAudio = () => {
    if (jitsiAPI && jitsiAPI.value) {
      jitsiAPI.value.executeCommand("toggleAudio");
    }
  };

  // Watch jitsiAPI for changes and log them

  return {
    inMeeting,
    meetingDiv,

    joinRoom,
    leaveRoom,

    jitsiAPI,

    toggleVideo,
    toggleAudio,
    isMuted,
    isVideoMuted,
    prepareId,
    prepareJoinRoom,
  };
});
